import type { TemplateDefinition } from "./types";

export const T01: TemplateDefinition = {
  id: "t01",
  module: 1,
  number: 1,
  name: "Personal Professional Standards Statement",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "Most officers operate by informal habits. A certified professional operates from a documented, deliberate, defensible framework. This statement is the foundational document of your LEP-C credential identity.",
  architectureContext:
    "This template serves the Sector-Sensitive Specialization and Value-Based Credentialing pillars. It establishes your professional identity as a documented, defensible framework rather than informal habit — the foundation upon which all other LEP-C templates build.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Professional Identity Declaration",
      fields: [
        {
          id: "identity_decl",
          type: "textarea",
          label: "Professional Identity Declaration",
          placeholder: "I am [Name], a certified law enforcement professional serving [Department/Agency]. My certification represents...",
          prefillFrom: "name",
        },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — Core Conduct Standards",
      description: "The specific conduct standards I commit to in the field every shift:",
      repeatable: {
        id: "conduct_standards",
        label: "Conduct Standard",
        maxRows: 8,
        initialRows: 1,
        addLabel: "+ Add Standard",
        fields: [
          { id: "standard", type: "text", label: "Standard", placeholder: "e.g., I will document every use of force with specific subject behavior language" },
          { id: "demonstration", type: "textarea", label: "How I demonstrate this in practice", placeholder: "Describe how this standard shows up in your daily work..." },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Community Commitment",
      fields: [
        { id: "community_obligations", type: "textarea", label: "My documented obligations to the communities I serve", placeholder: "Document your specific obligations to your community..." },
        { id: "community_actions", type: "textarea", label: "The specific actions I take each shift to fulfill this commitment", placeholder: "What you do every shift to serve your community..." },
      ],
    },
    {
      id: "s4",
      title: "Section 4 — Ethical Boundaries",
      fields: [
        { id: "ethical_lines", type: "textarea", label: "The professional and ethical lines I will not cross under any circumstances", placeholder: "Document your non-negotiable ethical boundaries..." },
        { id: "pressure_response", type: "textarea", label: "How I will respond when pressured by peers, supervisors, or circumstances to cross these lines", placeholder: "Your documented response to ethical pressure..." },
      ],
    },
    {
      id: "s5",
      title: "Section 5 — Renewal Commitment",
      fields: [
        { id: "renewal_commitment", type: "textarea", label: "My commitment to maintaining and growing my competency through the Sentinel 2-year renewal cycle", placeholder: "Your documented commitment to ongoing professional development..." },
        { id: "last_review", type: "date", label: "Last Review Date" },
        { id: "next_review", type: "date", label: "Next Scheduled Review Date", autoDateFrom: "last_review", autoDateOffsetMonths: 6 },
      ],
    },
  ],
};

export const T02: TemplateDefinition = {
  id: "t02",
  module: 1,
  number: 2,
  name: "Ethical Decision-Making Framework",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "Field ethics are not abstract. They play out in split-second decisions. This is your repeatable, documented 5-step framework for any situation involving ethical tension.",
  architectureContext:
    "This template operationalizes the Value-Based Credentialing pillar. It converts ethical principles into a repeatable field tool — ensuring your decisions are defensible, consistent, and documented.",
  sections: [
    {
      id: "step1",
      title: "Step 1 — Identify the Ethical Tension",
      variant: "steps",
      fields: [
        { id: "conflict_values", type: "textarea", label: "What two or more values are in conflict in this type of situation?", placeholder: "Identify the competing values..." },
        { id: "common_tensions", type: "textarea", label: "The most common ethical tensions I face in my current assignment", placeholder: "Describe the ethical tensions specific to your role..." },
      ],
    },
    {
      id: "step2",
      title: "Step 2 — Identify Who Is Affected and How",
      variant: "steps",
      fields: [
        { id: "stakeholders", type: "textarea", label: "Who are the stakeholders typically affected by my decisions in these situations?", placeholder: "List the stakeholders..." },
        { id: "stakeholder_impact", type: "textarea", label: "How does each stakeholder group experience the impact of my decision?", placeholder: "Describe how each group is affected..." },
      ],
    },
    {
      id: "step3",
      title: "Step 3 — Apply Constitutional and Policy Standard",
      variant: "steps",
      fields: [
        { id: "constitutional_standard", type: "text", label: "The constitutional standard that governs this category of decision", placeholder: "e.g., Fourth Amendment reasonableness standard" },
        { id: "policy_reference", type: "text", label: "The department policy or general order that applies", placeholder: "e.g., GO-308.02 Use of Force" },
        { id: "law_requires", type: "textarea", label: "What the law and policy require me to do", placeholder: "Document what the law and policy mandate..." },
      ],
    },
    {
      id: "step4",
      title: "Step 4 — Apply the Public Trust Test",
      variant: "steps",
      fields: [
        { id: "public_trust_radio", type: "radio", label: "Would this decision withstand full public scrutiny if reported in the media tomorrow?", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }, { label: "Depends on context", value: "depends" }] },
        { id: "public_trust_application", type: "textarea", label: "How I apply the public trust test before acting", placeholder: "Your documented approach to the public trust test..." },
      ],
    },
    {
      id: "step5",
      title: "Step 5 — Document and Report",
      variant: "steps",
      fields: [
        { id: "doc_obligation", type: "textarea", label: "My obligation to document this decision is", placeholder: "Document your documentation obligation..." },
        { id: "report_to", type: "text", label: "The person or system I report this decision to", placeholder: "e.g., Shift Supervisor / IA reporting system" },
        { id: "doc_protocol", type: "textarea", label: "My documentation protocol for ethical decisions", placeholder: "Your step-by-step documentation protocol..." },
      ],
    },
  ],
};

export const T03: TemplateDefinition = {
  id: "t03",
  module: 1,
  number: 3,
  name: "Sentinel Career Development Plan",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "A certified professional has a written career plan. This document maps your path from LEP-C through your target Sentinel level with specific milestones, CEC obligations, and upgrade triggers.",
  architectureContext:
    "This template serves the Scalable Growth Architecture pillar. It transforms career aspiration into a documented plan with measurable milestones, ensuring your Sentinel credential path is intentional and tracked.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Current Position Assessment",
      fields: [
        { id: "current_rank", type: "text", label: "Current Rank/Title", prefillFrom: "rank" },
        { id: "department", type: "text", label: "Department/Agency", prefillFrom: "department" },
        { id: "years_role", type: "number", label: "Years in Current Role", placeholder: "0" },
        { id: "current_certs", type: "textarea", label: "Current Certifications Held", placeholder: "List your current certifications..." },
        { id: "strengths", type: "textarea", label: "Current Strengths to Build On", placeholder: "Your professional strengths..." },
        { id: "gaps", type: "textarea", label: "Current Gaps to Address", placeholder: "Gaps you need to close..." },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — Target Level and Role",
      fields: [
        { id: "target_level", type: "select", label: "Target Sentinel Level", options: [{ label: "L2 LES-C", value: "l2" }, { label: "L3 LEM-C", value: "l3" }, { label: "L4 SLEE-C", value: "l4" }] },
        { id: "target_role", type: "text", label: "Target Role or Position Title", placeholder: "e.g., Sergeant / Patrol Supervisor" },
        { id: "why_target", type: "textarea", label: "Why this is my target level", placeholder: "Your rationale for this target..." },
        { id: "career_unlock", type: "textarea", label: "What this credential will unlock for my career", placeholder: "What this credential enables..." },
      ],
    },
    {
      id: "s3",
      title: "Section 3 — Timeline",
      description: "Visual timeline with 4 milestone markers:",
      repeatable: {
        id: "milestones",
        label: "Milestone",
        maxRows: 4,
        initialRows: 4,
        addLabel: "+ Add Milestone",
        fields: [
          { id: "label", type: "text", label: "Milestone Label", placeholder: "e.g., Complete LEP-C" },
          { id: "date", type: "date", label: "Target Date" },
          { id: "status", type: "select", label: "Status", options: [{ label: "Not Started", value: "not_started" }, { label: "In Progress", value: "in_progress" }, { label: "Complete", value: "complete" }] },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — CEC Fulfillment Plan",
      fields: [
        { id: "cec_requirement", type: "number", label: "CEC requirement per renewal cycle", prefill: "40" },
        { id: "renewal_date", type: "date", label: "Renewal date" },
        { id: "primary_cec", type: "select", label: "Primary CEC source", options: [{ label: "Sentinel Annual Renewal Plan $179/yr", value: "sentinel_renewal" }, { label: "Supplemental Modules", value: "supplemental" }, { label: "Both", value: "both" }] },
        { id: "secondary_cec", type: "textarea", label: "Secondary CEC sources", placeholder: "Other CEC sources..." },
        { id: "tracking_method", type: "textarea", label: "Tracking method", placeholder: "How you track CEC completion..." },
      ],
    },
    {
      id: "s5",
      title: "Section 5 — Upgrade Triggers",
      description: "I will enroll in the next Sentinel level when:",
      repeatable: {
        id: "upgrade_triggers",
        label: "Upgrade Trigger",
        maxRows: 5,
        initialRows: 1,
        addLabel: "+ Add Trigger",
        fields: [
          { id: "trigger", type: "text", label: "Trigger Condition", placeholder: "e.g., Promoted to Sergeant" },
        ],
      },
    },
  ],
};

export const T04: TemplateDefinition = {
  id: "t04",
  module: 1,
  number: 4,
  name: "Public Trust Strategy — Patrol Area",
  stage: "STAGE 1 — FOUNDATION",
  pillars: "Sector-Sensitive Specialization + Value-Based Credentialing",
  whyExists:
    "Community trust is a measurable operational outcome. This strategy document defines your approach to building it actively and systematically in your assigned patrol area.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It operationalizes community trust as a measurable, managed outcome — not an aspiration. Your patrol area becomes a managed trust portfolio.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Community Mapping",
      fields: [
        { id: "district", type: "text", label: "Patrol District Name/Identifier", prefillFrom: "district" },
      ],
      repeatable: {
        id: "community_segments",
        label: "Community Segment",
        maxRows: 8,
        initialRows: 1,
        addLabel: "+ Add Segment",
        fields: [
          { id: "name", type: "text", label: "Community Segment Name", placeholder: "e.g., Meridian Heights neighborhood" },
          { id: "size", type: "text", label: "Size/Reach estimate", placeholder: "e.g., ~2,500 residents" },
          { id: "relationship", type: "select", label: "Current relationship with law enforcement", options: [{ label: "Very Low", value: "very_low" }, { label: "Low", value: "low" }, { label: "Neutral", value: "neutral" }, { label: "Positive", value: "positive" }, { label: "Strong", value: "strong" }] },
          { id: "concerns", type: "text", label: "Primary concerns or issues", placeholder: "e.g., Vehicle break-ins, youth engagement" },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Trust Gap Analysis",
      fields: [
        { id: "trust_lowest", type: "textarea", label: "Where is trust lowest in my district and what is driving it?", placeholder: "Identify where and why trust is lowest..." },
        { id: "gap1", type: "text", label: "Gap 1", placeholder: "Most significant trust gap #1" },
        { id: "gap2", type: "text", label: "Gap 2", placeholder: "Most significant trust gap #2" },
        { id: "gap3", type: "text", label: "Gap 3", placeholder: "Most significant trust gap #3" },
        { id: "root_causes", type: "textarea", label: "The root causes behind these gaps", placeholder: "Underlying causes driving the trust gaps..." },
      ],
    },
    {
      id: "s3",
      title: "Section 3 — Engagement Actions",
      repeatable: {
        id: "engagement_actions",
        label: "Engagement Action",
        maxRows: 10,
        initialRows: 1,
        addLabel: "+ Add Action",
        fields: [
          { id: "action", type: "text", label: "Action Description", placeholder: "e.g., Weekly foot patrol through Meridian Heights business district" },
          { id: "target_segment", type: "text", label: "Target Community Segment", placeholder: "Which community segment" },
          { id: "frequency", type: "select", label: "Frequency", options: [{ label: "Daily", value: "daily" }, { label: "Weekly", value: "weekly" }, { label: "Monthly", value: "monthly" }, { label: "Quarterly", value: "quarterly" }, { label: "One-time", value: "one_time" }] },
          { id: "start_date", type: "date", label: "Start Date" },
          { id: "status", type: "select", label: "Status", options: [{ label: "Planned", value: "planned" }, { label: "Active", value: "active" }, { label: "Complete", value: "complete" }] },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Measurement Plan",
      fields: [
        { id: "measure_how", type: "textarea", label: "How I will measure whether trust is improving", placeholder: "Your measurement approach..." },
        { id: "trust_indicator", type: "text", label: "My primary trust indicator metric", placeholder: "e.g., Community survey satisfaction score" },
        { id: "target_90", type: "textarea", label: "My 90-day trust target", placeholder: "90-day measurable target..." },
        { id: "target_1yr", type: "textarea", label: "My 1-year trust target", placeholder: "1-year measurable target..." },
        { id: "report_progress", type: "textarea", label: "How I will report progress to my supervisor", placeholder: "Your reporting protocol..." },
      ],
    },
  ],
};
